import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use same-origin API path to avoid cross-domain issues in production
// This ensures /api calls always go to the current domain
const API = (function() {
  var envUrl = process.env.REACT_APP_BACKEND_URL;
  // In production, if the env URL doesn't match current origin, use relative path
  if (typeof window !== 'undefined' && envUrl) {
    var currentOrigin = window.location.origin;
    // If env URL points to a different domain (stale build), use current origin
    if (!envUrl.includes('localhost') && !envUrl.includes(window.location.hostname)) {
      return currentOrigin + '/api';
    }
  }
  return envUrl ? envUrl + '/api' : '/api';
})();

// Helper to get/set token in localStorage (fallback for mobile)
const getStoredToken = () => localStorage.getItem('auth_token');
const setStoredToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
};

// Configure axios to send token in header as fallback
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normalize FastAPI/Pydantic error responses so that err.response.data.detail
// is ALWAYS a string. Prevents React crash (#31) when components naively render
// the detail object/array as a JSX child via toast.error() or similar.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const data = error?.response?.data;
      if (data && data.detail !== undefined && data.detail !== null) {
        const d = data.detail;
        if (typeof d !== 'string') {
          let formatted;
          if (Array.isArray(d)) {
            // Pydantic 422 validation errors
            formatted = d.map((e) => {
              if (typeof e === 'string') return e;
              const field = Array.isArray(e?.loc) ? e.loc.slice(1).join('.') : '';
              const msg = e?.msg || 'inválido';
              return field ? `${field}: ${msg}` : msg;
            }).filter(Boolean).join(' · ') || 'Error de validación';
          } else if (typeof d === 'object') {
            formatted = d.msg || JSON.stringify(d);
          } else {
            formatted = String(d);
          }
          // Preserve raw detail too for advanced consumers
          error.response.data._rawDetail = d;
          error.response.data.detail = formatted;
        }
      }
    } catch (_e) { /* never let interceptor itself throw */ }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sessionProcessed = useRef(false);

  const checkAuth = useCallback(async () => {
    // Skip checkAuth if a session was just processed (avoid race condition)
    if (sessionProcessed.current) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      setError(null);
      // If response includes token, update localStorage
      if (response.data.token) {
        setStoredToken(response.data.token);
      }
    } catch (err) {
      // If cookie auth fails, try with stored token
      const token = getStoredToken();
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setError(null);
          // Update token if provided in response
          if (response.data.token) {
            setStoredToken(response.data.token);
          }
          return;
        } catch (tokenErr) {
          setStoredToken(null);
        }
      }
      // Only clear user if session wasn't just processed
      if (!sessionProcessed.current) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Legacy alias for Google login
  const login = loginWithGoogle;

  const loginWithEmail = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Store token in localStorage as fallback for mobile
      if (response.data.token) {
        setStoredToken(response.data.token);
      }
      
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      throw new Error(message);
    }
  };

  const registerWithEmail = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      
      // Store token in localStorage as fallback for mobile
      if (response.data.token) {
        setStoredToken(response.data.token);
      }
      
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Registration error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      
      let message = 'Error al registrarse. Por favor, intenta de nuevo.';
      
      if (err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err.response?.status === 400) {
        message = 'Datos de registro inválidos. Verifica tu información.';
      } else if (err.response?.status === 409) {
        message = 'Este email ya está registrado. Intenta iniciar sesión.';
      } else if (err.response?.status === 422) {
        message = 'Formato de email inválido.';
      } else if (err.response?.status >= 500) {
        message = 'Error del servidor. Por favor intenta en unos minutos.';
      } else if (!err.response) {
        message = 'Error de conexión. Verifica tu internet e intenta de nuevo.';
      }
      
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setStoredToken(null);
      window.location.href = '/';
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null);
      setStoredToken(null);
      window.location.href = '/';
    }
  };

  const processSession = async (sessionId) => {
    try {
      sessionProcessed.current = true;
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      
      // Store token in localStorage as fallback for mobile
      if (response.data.token) {
        setStoredToken(response.data.token);
      }
      
      setUser(response.data);
      setLoading(false);
      return response.data;
    } catch (err) {
      sessionProcessed.current = false;
      setError('Error de autenticación');
      throw err;
    }
  };

  const registerRole = async (role) => {
    try {
      const token = getStoredToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API}/auth/register-role`,
        { role },
        { 
          withCredentials: true,
          headers 
        }
      );
      await checkAuth();
    } catch (err) {
      console.error('registerRole error:', err.response?.data || err.message);
      throw err;
    }
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      loginWithGoogle,
      loginWithEmail,
      registerWithEmail,
      logout,
      processSession,
      registerRole,
      checkAuth,
      clearError,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isSales: user?.role === 'sales' || user?.role === 'admin',
      isProfessional: user?.role === 'professional',
      isCenter: user?.role === 'center'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
